import React, { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Stack, useMediaQuery } from "@mui/material";

import { Button as GradientButton } from "@ui/index";

import { useCaptcha } from "@hooks/useCaptcha";

import { login, register } from "@store/actions/user";
import { useAppDispatchUnwrap } from "@store/hooks";

import { Captcha, CAPTCHA_ENABLED } from "@components/Captcha";
import FormikInput from "@components/FormikInput";
import PasswordRequirements from "@components/PasswordRequirements";
import { IError } from "@models/inner-models";
import { ERoutes } from "@utils/constants";
import { isStrongPassword } from "@utils/passwordPolicy";
import { Formik } from "formik";
import styled, { css, keyframes } from "styled-components";
import { object, string } from "yup";

import { BottomFade, ScrollArea, ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import {
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  SectionLabel,
} from "@/components/CartModal/styles";
import { Link } from "@/ui-kit";
import { normalizePersonName, stripNonNameCharacters } from "@/utils/normalizePersonName";

import { HaveAnAccountTypography } from "./components";

/** Phone-only compaction for the signup "Review & Accept" block — shrinks the
    checkbox rows/box/label + section divider so the submit button and the
    "already have an account" link fit without scrolling. Scoped to this
    wrapper so the shared cart checkboxes are untouched. */
const SignupReviewCompact = styled.div`
  width: 100%;

  @media (max-width: 600px) {
    ${SectionLabel} {
      margin-top: 6px;
      margin-bottom: 8px;
    }
    ${CheckboxRow} {
      padding: 10px 14px;
      gap: 10px;
      margin-bottom: 8px;
    }
    ${CheckboxBox} {
      width: 20px;
      height: 20px;
    }
    ${CheckboxLabel} {
      font-size: 0.78rem;
      line-height: 1.4;
    }
  }
`;

interface IForm {
  isSignUp: boolean;
  handleClose: () => void;
  onForgotPassword: () => void;
  onLayoutChange?: () => void;
  /** Triggered when the backend returns ACCOUNT_SCHEDULED_FOR_DELETION
      on login — the user's account is in the 30-day grace window. The
      parent dialog hides itself + shows the AccountRestoreModal. */
  onAccountScheduledForDeletion?: (info: {
    permanentDeleteOn: string | null;
    userEmail: string | null;
    hadSubscription: boolean;
    subEndsOn: string | null;
    sessionTokens: {
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresAt?: string;
      refreshTokenExpiresAt?: string;
    };
  }) => void;
  /** Notice shown above the form (e.g. "Your password has been changed,
      please log in with your new password"). Controlled by LoginDialog
      so both pathways — sessionStorage-on-mount and recovery-success
      in-dialog — feed the same UI. Naturally cleared on successful
      login by the full-page reload in the login thunk. */
  loginNotice: string;
  /** Switch back to the login panel. On phone signup the "have an account"
      link lives in this form's fixed footer (so it stays visible with the
      button), so the form needs the callback. */
  onSwitchToLogin?: () => void;
}

/** Shake keyframes for the "you submitted with empty/invalid fields"
 *  feedback. ~6px lateral wobble x4, 400ms total. Subtle but unmistakable. */
const shakeKeyframes = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
`;

/** Wraps both email + password inputs. When `$shake` flips true, the
 *  wrapper plays one shake animation, then the parent flips it back via
 *  onAnimationEnd. `$invalid` controls a persistent red border on the
 *  inputs inside (cleared when the user focuses either field), so the
 *  visual cue lingers even after the animation finishes. */
const InputsShakeWrap = styled.div<{ $shake: boolean; $invalid: boolean }>`
  width: 100%;

  ${({ $shake }) => $shake && css`
    animation: ${shakeKeyframes} 0.4s ease-in-out;
  `}

  ${({ $invalid }) => $invalid && css`
    .MuiOutlinedInput-root {
      border-color: #e47a6a !important;
    }
    ${CheckboxBox} {
      border-color: #e47a6a !important;
    }
  `}
`;

/** One-shot shake (+ optional red input border) for a single block — the terms
 *  checkbox and the firstName field. */
const ShakeBlock = styled.div<{ $shake: boolean; $invalid?: boolean; $areaInvalid?: boolean }>`
  ${({ $shake }) => $shake && css`
    animation: ${shakeKeyframes} 0.4s ease-in-out;
  `}

  ${({ $invalid }) => $invalid && css`
    .MuiOutlinedInput-root {
      border-color: #e47a6a !important;
    }
  `}

  /* Red outline around the whole block (the terms row's area) — box-shadow so it
     doesn't shift layout. */
  ${({ $areaInvalid }) => $areaInvalid && css`
    border-radius: 8px;
    box-shadow: 0 0 0 1px #e47a6a;
  `}
`;

/** Smooth entrance for the account-lockout banner — a short opacity + slide
 *  fade-in (no backdrop-filter here, so an opacity fade is safe and reads as
 *  the calm, non-alarming cue we want). */
const bannerFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** Login rate-limit / lockout notice. Amber, centered, one short line — fades in
 *  when the backend returns TOO_MANY_ATTEMPTS (too many failed logins). */
const LockoutBanner = styled.div`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 169, 68, 0.12);
  border: 1px solid #ffa944;
  border-radius: 8px;
  text-align: center;
  animation: ${bannerFadeIn} 0.4s ease;
`;

const ForgotBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-family: "Raleway", sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

/** Plain text-link styled component used for the always-visible
 *  "Forgot your email?" / "Forgot password?" helpers below the password
 *  field. Subtler than `ForgotBtn` (which sits inside the orange error
 *  banner and needs more visual weight). */
const InlineHelpLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #c9a987;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #ebc28e;
    text-decoration: underline;
  }
`;

const validationSchema = (t: (key: string) => string, isSignUp: boolean) => {
  if (isSignUp) {
    return object({
      email: string().email(t("emailValidation")).required(t("fieldRequired")),
      // Full policy, not just length — mirrors the server rule so the form
      // can't accept something the API will reject. See utils/passwordPolicy.
      password: string()
        .required(t("fieldRequired"))
        .test("strength", t("passwordValidation"), (value) => isStrongPassword(value ?? "")),
      // First name policy: single word, no spaces (hyphens fine — "Anna-Maria").
      // Mirrors the newsletter forms + the account-page name editor.
      firstName: string().required(t("fieldRequired")).matches(/^\S+$/, t("noSpaces")),
    });
  }

  return object({
    email: string().required(t("fieldRequired")),
    password: string().required(t("fieldRequired")),
  });
};

const Form: FC<IForm> = ({ isSignUp, handleClose, onForgotPassword, onLayoutChange, loginNotice, onAccountScheduledForDeletion, onSwitchToLogin }) => {
  const { t } = useTranslation("login");
  const { t: tValidation } = useTranslation("validation");

  // Phone create-account: the panel is taller than a small screen, so instead
  // of scrolling the whole panel (button included) we use the modal template's
  // "variant (a)" — the fields scroll in a capped body and the submit button
  // (+ "have an account" link) live in an always-visible fixed footer.
  const isPhone = useMediaQuery("(max-width:600px)");
  // The signup panel scrolls at EVERY width, not just on a phone. It used to be
  // gated on `isPhone`, which meant a short laptop or tablet window simply cut
  // the form off with no scroll and no hint — and the password requirements
  // checklist made the panel taller, so it started happening on ordinary
  // screens. Widening it is safe: the cap is a dvh calc, so on a tall viewport
  // it is never reached, nothing overflows, `canScrollDown` stays false, and
  // the fade/hint stay hidden. Desktop looks exactly as it did.
  const scrollableSignup = isSignUp;
  // Scroll affordance for the signup body — the mandatory bottom fade +
  // bouncing "Scroll ⌄" hint show only while there's more content below.
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const canScrollDown = useScrollAffordance(scrollBodyRef, [scrollableSignup, isPhone]);

  const [loginError, setLoginError] = useState("");
  // Empty/invalid submit feedback. `shake` runs one animation cycle (reset
  // via onAnimationEnd) so a second click during the animation still
  // re-triggers cleanly. `invalid` is the persistent red border that stays
  // until the user focuses an input again — same wipe trigger as
  // `loginError` so the visual cue and the message clear together.
  const [shake, setShake] = useState(false);
  const [invalid, setInvalid] = useState(false);
  // Account-lockout banner — set when the backend returns TOO_MANY_ATTEMPTS
  // (too many failed logins in the window). Fades in a short amber notice;
  // cleared when the user re-focuses a field or on the next submit.
  const [lockedOut, setLockedOut] = useState(false);
  // Captcha gating: `captchaSolved` drives the submit button's dimmed→gold flip
  // (set via the widget's onChange). The captcha is invisible + auto-solving, so
  // there's no separate shake / red-ring / fade-in state anymore.
  const [captchaSolved, setCaptchaSolved] = useState(false);
  // Terms shake (mirrors the captcha) — the checkbox wobbles + reddens when the
  // user clicks the dimmed submit without accepting terms.
  const [shakeTerms, setShakeTerms] = useState(false);
  /**
   * Shakes the name field when a character that cannot appear in a name is
   * typed — a digit, a full stop, a comma, a space.
   *
   * Separate from `shake` (which fires on a failed submit) so the two cannot
   * interfere, and deliberately NOT wired to `$invalid`: the field must move
   * without turning red. A red border says "you have made a mistake and it is
   * still there"; nothing is still there, because the character was refused as
   * it arrived. The shake says "not that one" and the field stays clean.
   */
  const [shakeName, setShakeName] = useState(false);
  // Persistent red border on the terms checkbox after a submit attempt without
  // accepting (cleared when the user toggles the box).
  const [termsInvalid, setTermsInvalid] = useState(false);
  // Build-time flag: prod has a site key (widget required), dev usually doesn't.
  const captchaRequired = CAPTCHA_ENABLED;
  // Toggle for the inline "Forgot your email?" help block. There's no
  // self-serve recovery (email IS the account identity, so we can't email
  // someone we can't address) — clicking surfaces a short note pointing
  // to the manual support path documented in security.md.
  const [showEmailHelp, setShowEmailHelp] = useState(false);
  useEffect(() => { setLoginError(""); }, [isSignUp]);
  // Note: we DON'T auto-clear loginNotice on mount of the signup Form.
  // Panels 0 (login) and 1 (signup) are both always mounted; the signup
  // Form would otherwise wipe a freshly-set notice on every parent
  // re-render (because `onClearLoginNotice` is a new function each
  // render). The notice is already gated on `!isSignUp` in the render
  // path so users on the signup tab never see it anyway; it'll naturally
  // clear via `clearLoginNotice` on successful login (handled in the
  // login submit path). Only the email-help collapse is signup-bound.
  useEffect(() => { if (isSignUp) setShowEmailHelp(false); }, [isSignUp]);
  useEffect(() => { onLayoutChange?.(); }, [loginError, loginNotice, showEmailHelp, captchaSolved, lockedOut]);
  // Default OFF — newsletter consent must be an active opt-in (GDPR / CJEU
  // Planet49). The single checkbox opts the user into the General newsletter
  // + Promotional list; they can fine-tune per-list on /account/notifications.
  const [isNewsletterChecked, setIsNewsletterChecked] = useState<boolean>(false);
  const [areTermsChecked, setAreTermsChecked] = useState<boolean>(false);

  const initialValues = useMemo(() => {
    if (isSignUp) {
      return {
        firstName: "",
        email: "",
        password: "",
        hasAcceptedTerms: false,
        hasNewsletter: false,
      };
    } else {
      return {
        email: "",
        password: "",
      };
    }
  }, [isSignUp]);

  const handleNewsletter = (event: ChangeEvent<HTMLInputElement>) => {
    setIsNewsletterChecked(event.target.checked);
  };

  const handleTerms = (event: ChangeEvent<HTMLInputElement>) => {
    setAreTermsChecked(event.target.checked);
    setTermsInvalid(false);
  };

  const dispatch = useAppDispatchUnwrap();
  // S-004 — reCAPTCHA on signup. The <Captcha> widget (rendered below for signup)
  // wires captchaRef; its token is sent to /auth/register and verified server-side.
  const { captchaRef } = useCaptcha();

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={isSignUp ? () => validationSchema(tValidation, isSignUp) : undefined}
      onSubmit={async (values) => {
        setLoginError("");
        setLockedOut(false);
        if (isSignUp) {
          // onSubmit only fires once Formik's field validation passes (the inputs
          // are valid here), so terms + captcha are the remaining gates. Both give
          // the same shake feedback as the inputs instead of an error line.
          if (!areTermsChecked) {
            setTermsInvalid(true);
            setShakeTerms(false);
            requestAnimationFrame(() => setShakeTerms(true));
            return;
          }
          // S-004 — when a reCAPTCHA site key is configured (prod), the user must
          // solve it; the token is also re-verified server-side.
          const captchaToken = (captchaRef.current?.getValue() as string) || undefined;
          if (captchaRequired && !captchaToken) {
            // The invisible widget auto-solves and reveals itself if it can't;
            // just block the submit until a token exists.
            return;
          }
          try {
            await dispatch(
              register({
                ...values,
                hasAcceptedTerms: areTermsChecked,
                // One checkbox opts into the Newsletter (General) + Promotional.
                hasGeneralNewsletter: isNewsletterChecked,
                promoEmailsEnabled: isNewsletterChecked,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                captchaToken,
              }),
            );
            captchaRef.current?.reset();
            handleClose();
          } catch (err) {
            // Reset the (single-use) captcha and re-dim the button so a retry
            // re-solves it.
            captchaRef.current?.reset();
            setCaptchaSolved(false);
            const message = (err as IError).message;
            setLoginError(message);
          }
        }

        if (!isSignUp) {
          const triggerShake = () => {
            // No error text — just shake the inputs + flag them invalid
            // so they get a red border until the user focuses one again.
            // Resetting `shake` first ensures the animation re-triggers
            // even on rapid repeat submits (state stays true, so the
            // setter is a no-op without this reset).
            setShake(false);
            requestAnimationFrame(() => setShake(true));
            setInvalid(true);
          };

          if (!values.email || !values.password) {
            triggerShake();
            return;
          }
          try {
            await dispatch(login(values));
            handleClose();
          } catch (err: any) {
            // Detect the soft-deleted-in-grace state: server returns
            // ACCOUNT_SCHEDULED_FOR_DELETION with session tokens in
            // details so the upcoming /restore-from-deletion POST can
            // authenticate. Bubble up to the parent dialog to show the
            // blocking restore modal instead of the shake.
            if (err?.code === "ACCOUNT_SCHEDULED_FOR_DELETION" && err?.details) {
              const d = err.details as Record<string, string>;
              if (d.accessToken && d.refreshToken && onAccountScheduledForDeletion) {
                onAccountScheduledForDeletion({
                  permanentDeleteOn: d.permanentDeleteScheduledAt
                    ? new Date(d.permanentDeleteScheduledAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
                    : null,
                  userEmail: values.email ?? null,
                  hadSubscription: String(d.hadSubscription) === "true",
                  subEndsOn: d.subEndsOn ?? null,
                  sessionTokens: {
                    accessToken: d.accessToken,
                    refreshToken: d.refreshToken,
                    accessTokenExpiresAt: d.accessTokenExpiresAt,
                    refreshTokenExpiresAt: d.refreshTokenExpiresAt,
                  },
                });
                return;
              }
            }
            // Too many failed logins → the account is briefly locked. Show the
            // calm fade-in banner instead of the shake (this IS a message the
            // user needs: retrying now won't work, they have to wait).
            if (err?.code === "TOO_MANY_ATTEMPTS") {
              setLockedOut(true);
              return;
            }
            // Both empty-fields and wrong-credentials surface the same
            // shake/red-border feedback. The distinction doesn't help the
            // user (the action is the same: re-enter and resubmit), and a
            // generic "wrong credentials" banner is a security/UX wash
            // anyway — modern auth surfaces favour silent failure cues
            // over verbose messages that confirm one half of the pair.
            triggerShake();
          }
        }
      }}
    >
      {({ handleSubmit, isSubmitting, dirty, isValid, values, errors, touched, setFieldError, setFieldTouched }) => {
        const clearFieldError = (field: string) => {
          setFieldError(field, undefined);
          setFieldTouched(field, false, false);
          setLoginError("");
        };

        // Submit-button "ready" state → drives the dimmed (grey, still clickable)
        // → gold flip. Signup needs valid inputs + terms + a solved captcha (when
        // required); login just needs both fields filled. When not ready the
        // button stays dimmed and a click shakes whatever's missing.
        const captchaSatisfied = !captchaRequired || captchaSolved;
        const ready = isSignUp
          ? areTermsChecked && isValid && dirty && captchaSatisfied
          : !!(values.email && values.password);

        const submitButton = (
          <GradientButton
            isBigButton
            type="submit"
            // Always clickable (only disabled mid-submit) so the dimmed button
            // shakes whatever's missing on click — same as login. Signup's
            // terms + captcha gates shake from onSubmit; the inputs shake here
            // (Formik blocks onSubmit when they're invalid, so onSubmit can't).
            isDisabled={isSubmitting}
            // Grey-but-clickable until everything (incl. the captcha) is done.
            isDimmed={!ready}
            isFullWidth
            onClick={() => {
              // Dimmed-but-clickable: shake EVERY incomplete required element on
              // click. Formik blocks the real submit when inputs are invalid, so
              // onSubmit can't be relied on for the feedback — do it all here
              // (mirrors the cart's "shake whatever's missing").
              if (!isSignUp) return;
              if (!isValid || !dirty) {
                setShake(false);
                requestAnimationFrame(() => setShake(true));
              }
              if (!areTermsChecked) {
                setTermsInvalid(true);
                setShakeTerms(false);
                requestAnimationFrame(() => setShakeTerms(true));
              }
            }}
          >
            {t(isSignUp ? "getAccess" : "login")}
          </GradientButton>
        );

        const scrollBody = (
          <Stack
            ref={scrollBodyRef}
            spacing="16px"
            alignItems="flex-start"
            width="100%"
            // Signup: this is the ONLY scroll area; the button below it is fixed.
            // Reserve the scrollbar's own gutter so it sits beside the fields, not
            // under them, and give it a thin visible thumb.
            sx={
              scrollableSignup
                ? {
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    // Small horizontal inset so the terms checkbox's red ring
                    // (a 1px box-shadow) isn't clipped at the body edges by
                    // overflow-x:hidden.
                    px: "4px",
                    // Hide the native scrollbar — the bottom fade + bouncing
                    // "Scroll" hint are the affordance (iOS overlay scrollbars
                    // can't be reserved beside the content anyway).
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }
                : undefined
            }
          >
            <Stack spacing="16px" width="100%">
              {/* One-time success notice (e.g. after the user completes a
                  password change / reset elsewhere in the app). Sits ABOVE
                  the email field so it's the first thing the user reads
                  when the dialog opens, framing the rest of the form. */}
              {loginNotice && !isSignUp && (
                <div style={{
                  width: "100%",
                  padding: "14px 16px",
                  background: "rgb(76, 175, 80)",
                  borderRadius: "8px",
                  textAlign: "center",
                }}>
                  <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.85rem", color: "#ffffff" }}>
                    {loginNotice}
                  </span>
                </div>
              )}

              {lockedOut && !isSignUp && (
                <LockoutBanner>
                  <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.85rem", color: "#ffffff" }}>
                    Too many attempts. Please wait a few minutes and try again.
                  </span>
                </LockoutBanner>
              )}

              {isSignUp && (
                <ShakeBlock
                  $shake={shake || shakeName}
                  $invalid={!!errors.firstName && !!touched.firstName}
                  // The space key used to be swallowed here with no feedback at
                  // all, which reads as a broken keyboard. Spaces are now
                  // refused by the same path as every other disallowed
                  // character, so they get the same shake — one rule, one
                  // response, instead of a special case that stays silent.
                  onAnimationEnd={() => setShakeName(false)}
                >
                  <FormikInput
                    fieldName="firstName"
                    label={t("firstName")}
                    placeholder={t("firstName") ?? ""}
                    autoComplete="name"
                    // Capitalises as they type: patrick -> Patrick,
                    // anne-marie -> Anne-Marie, and PATRICK -> Patrick. The
                    // server applies the identical rule (see
                    // common/validation/normalize-name.ts), so this is a
                    // courtesy rather than the enforcement — calling the API
                    // directly gets the same result.
                    transformValue={(raw) => {
                      // Refuse anything a name cannot contain — digits, dots,
                      // commas, spaces, symbols — and shake to say so. The
                      // character never lands in the field, so there is nothing
                      // to mark as invalid.
                      const kept = stripNonNameCharacters(raw);
                      if (kept.length !== raw.length) setShakeName(true);
                      return normalizePersonName(kept);
                    }}
                    hideRequiredMessage
                    sx={{ "& .MuiOutlinedInput-root": { "&.Mui-focused": { borderColor: "rgba(235,194,142,0.5)" } } }}
                    inputRef={(ref: HTMLInputElement) => ref?.addEventListener("focus", () => clearFieldError("firstName"))}
                  />
                </ShakeBlock>
              )}

              {/* Wrap email + password in a nested Stack so the parent's
                  16px spacing still applies between this group and its
                  siblings, and the inner Stack handles the gap BETWEEN
                  email and password. The single onFocusCapture handler on
                  the wrapper drives both "clear field error" and "close
                  email help" behaviours without leaking an event listener
                  per render the way `inputRef.addEventListener` does in
                  FormikInput's render path. */}
              <InputsShakeWrap
                $shake={shake}
                $invalid={invalid && !isSignUp}
                onAnimationEnd={() => setShake(false)}
                onFocusCapture={(e) => {
                  // React synthesises focus events via a single root-level
                  // listener — no per-render accumulation regardless of how
                  // many times the Form re-renders.
                  const name = (e.target as HTMLInputElement)?.name;
                  if (name === "email" || name === "password") clearFieldError(name);
                  if (showEmailHelp) setShowEmailHelp(false);
                  // Clear the "invalid" red-border state the moment the
                  // user re-engages with either field.
                  if (invalid) setInvalid(false);
                  if (lockedOut) setLockedOut(false);
                }}
              >
                <Stack spacing="16px" width="100%">
                <FormikInput
                  fieldName="email"
                  label={t("email")}
                  placeholder={t("email") ?? ""}
                  autoComplete="email"
                  type={isSignUp ? "email" : "text"}
                  hideRequiredMessage={isSignUp}
                />

                <FormikInput
                  fieldName="password"
                  label={t("password")}
                  placeholder={isSignUp ? t("password") + (t("minPasswordLength") ?? "") : t("password") ?? ""}
                  autoComplete={isSignUp ? "new-password" : "password"}
                  type="password"
                  hideRequiredMessage={isSignUp}
                  hideErrorText={isSignUp}
                  // Red border for as long as the password does not qualify,
                  // matching the red rules below it. Only once there is
                  // something typed — an empty field is not yet wrong.
                  forceError={isSignUp && values.password.length > 0 && !isStrongPassword(values.password)}
                />
                {/* Sign-up only: on login the rules are irrelevant (the
                    password already exists) and would just be noise. */}
                {isSignUp && <PasswordRequirements password={values.password} tone="error" />}
                </Stack>
              </InputsShakeWrap>

              {/* Always-visible recovery links — login mode only. The
                  "Forgot password?" link routes to the existing recovery
                  dialog. "Forgot your email?" toggles an inline note since
                  there's no self-serve recovery path for the address (email
                  IS the identity). Sits in the same row, password-aligned. */}
              {!isSignUp && (
                <Stack spacing="8px" width="100%" sx={{ marginTop: "-4px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <InlineHelpLink
                      type="button"
                      onClick={() => setShowEmailHelp((v) => !v)}
                      aria-expanded={showEmailHelp}
                    >
                      Forgot your email?
                    </InlineHelpLink>
                    <InlineHelpLink type="button" onClick={onForgotPassword}>
                      Forgot password?
                    </InlineHelpLink>
                  </div>
                  {showEmailHelp && (
                    <div style={{
                      padding: "14px 16px",
                      background: "rgba(156, 211, 253, 0.06)",
                      border: "1px solid rgba(156, 211, 253, 0.22)",
                      borderRadius: "8px",
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: "0.9375rem",
                      lineHeight: 1.55,
                      color: "rgba(251, 244, 236, 0.9)",
                    }}>
                      <a href={ERoutes.Contact} style={{ color: "#9cd3fd" }}>Send us a message</a> with your name and anything that can help us identify your account (a recent order number, a YouTube channel you connected, etc.) — and we&apos;ll help you recover it.
                    </div>
                  )}
                </Stack>
              )}
            </Stack>

            {isSignUp && (
              <SignupReviewCompact>
              <Stack spacing="12px" width="100%" sx={{ marginBottom: "8px" }}>
                <SectionLabel style={{ marginTop: "8px", marginBottom: "4px" }}>
                  <span>Review & Accept</span>
                </SectionLabel>

                <Stack spacing="4px">
                  {/* Marketing opt-in first + highlighted; Terms second + plain. Both keep the
                      same padding/geometry (transparent 1px border on Terms) so the boxes align. */}
                  <CheckboxRow
                    onClick={() => handleNewsletter({ target: { checked: !isNewsletterChecked } } as any)}
                    style={{ background: "rgba(235, 194, 142, 0.1)", border: "1px dashed rgba(235, 194, 142, 0.5)", marginBottom: "4px" }}
                  >
                    <CheckboxBox $checked={isNewsletterChecked}>
                      <svg viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </CheckboxBox>
                    <CheckboxLabel>
                      <Trans t={t} i18nKey="newsletterCheckbox" />
                    </CheckboxLabel>
                  </CheckboxRow>

                  <ShakeBlock $shake={shakeTerms} $areaInvalid={termsInvalid} onAnimationEnd={() => setShakeTerms(false)}>
                  <CheckboxRow
                    onClick={() => handleTerms({ target: { checked: !areTermsChecked } } as any)}
                    style={{ background: "transparent", border: "1px solid transparent" }}
                  >
                    <CheckboxBox $checked={areTermsChecked}>
                      <svg viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </CheckboxBox>
                    <CheckboxLabel>
                      <Trans
                        t={t}
                        i18nKey="acceptTermsCheckbox"
                        components={[
                          <a href={ERoutes.Conditions} target="_blank" rel="noreferrer" />,
                          <a href={ERoutes.Privacy} target="_blank" rel="noreferrer" />,
                          // Third, matching the checkout checkbox word for word
                          // and link for link. The two sentences saying
                          // different things was the oddity worth removing.
                          <a href={ERoutes.LicenceTerms} target="_blank" rel="noreferrer" />,
                          <a href="/refund-policy" target="_blank" rel="noreferrer" />,
                        ]}
                      />
                    </CheckboxLabel>
                  </CheckboxRow>
                  </ShakeBlock>
                </Stack>
              </Stack>
              </SignupReviewCompact>
            )}

            {loginError && (
              <div style={{
                width: "100%",
                padding: "14px 16px",
                background: "rgba(255, 169, 68, 0.12)",
                border: "1px solid #FFA944",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}>
                <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.85rem", color: "#ffffff" }}>
                  {loginError}
                </span>
                {!isSignUp && (
                  <ForgotBtn type="button" onClick={onForgotPassword}>
                    Forgot password?
                  </ForgotBtn>
                )}
              </div>
            )}

            {/* Invisible captcha: auto-solves in the background and takes no
                space, so the button sits right under the fields. It only reveals
                itself if the solve errors or stalls. */}
            {isSignUp && (
              <Captcha hideUntilNeeded onChange={(token) => setCaptchaSolved(!!token)} />
            )}

            {/* Phone signup keeps the button in the fixed footer below; every
                other case renders it inline at the end of the form. */}
            {!scrollableSignup && submitButton}
          </Stack>
        );

        return (
          <form
            onSubmit={handleSubmit}
            noValidate
            style={{
              width: "100%",
              zIndex: 5,
              // Signup: become a flex column capped to the viewport so the body
              // scrolls and the footer (button + link) stays pinned. dvh, not
              // vh, so iOS Safari's collapsing toolbar doesn't hide the footer.
              ...(scrollableSignup ? { display: "flex", flexDirection: "column", maxHeight: "calc(100dvh - 150px)" } : null),
            }}
          >
            {scrollableSignup ? (
              <>
                {/* Scroll body + the mandatory bottom fade / bouncing "Scroll ⌄"
                    hint (shown only while there's more content below). */}
                <ScrollArea>
                  {scrollBody}
                  {/* z-index lifts the fade + hint above the reCAPTCHA iframe,
                      which otherwise paints over them. */}
                  <BottomFade $visible={canScrollDown} aria-hidden="true" style={{ zIndex: 6 }}>
                    <ScrollHint>
                      <span>Scroll</span>
                      <ScrollChevron />
                    </ScrollHint>
                  </BottomFade>
                </ScrollArea>

                {/* Always-visible fixed footer (button + "have an account" link)
                    — the body above scrolls under the fade. */}
                <Stack sx={{ flexShrink: 0, width: "100%", paddingTop: "16px", paddingBottom: "4px" }}>
                  {submitButton}
                  {onSwitchToLogin && (
                    // Explicit gap (inline margin beats the <p> default AND any
                    // Stack-injected margin, which is why a Stack `spacing` here
                    // read as zero) — keeps clear space between button and link.
                    <HaveAnAccountTypography style={{ marginTop: "18px", marginBottom: 0 }}>
                      <Trans
                        t={t}
                        i18nKey="haveAnAccount"
                        components={{ linkToLogin: <Link component="button" size={"inherit"} onClick={onSwitchToLogin} /> }}
                      />
                    </HaveAnAccountTypography>
                  )}
                </Stack>
              </>
            ) : (
              scrollBody
            )}
          </form>
        );
      }}
    </Formik>
  );
};

export default Form;
